import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { errorInfo, formatDate } from "../lib/collaborators";
import {
  ACCREDITATION_STATE_CLASSES, ACCREDITATION_STATE_LABELS, LEVEL_CODES, MIN_REVOKE_REASON, isGrantFormValid, splitAccreditations,
} from "../lib/skills";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { BadgeCheck, ChevronDown, ChevronRight, Loader2, Plus } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// Habilitations d'une fiche : ce que la personne est AUTORISÉE à faire au nom de Jamora, décidé par quelqu'un, daté,
// éventuellement limité dans le temps, jamais effacé. Accorder et révoquer demandent « accreditations:grant » et ne sont jamais
// possibles sur sa propre fiche, administrateur compris (le serveur le refuse quoi qu'il arrive).
export default function CollaboratorAccreditations({ fiche, refs, canGrant, onHistoryChange }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading");
  const [showPast, setShowPast] = useState(false);
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/collaborators/${fiche.id}/accreditations`);
      setItems(data.items);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, [fiche.id]);

  useEffect(() => {
    load();
  }, [load]);

  const done = async () => {
    await load();
    onHistoryChange?.();
  };

  const allowed = canGrant && !fiche.is_me;
  const eligible = !fiche.archived && fiche.status !== "exited";
  const { current, past } = splitAccreditations(items);

  return (
    <Card className="p-5 space-y-3" data-testid="collab-accreditations">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-semibold text-sm text-slate-900"><BadgeCheck size={15} /> Habilitations</div>
        {allowed && eligible && refs.skills.some((s) => s.active) && (
          <Button variant="outline" size="sm" onClick={() => setGranting(true)} data-testid="btn-accreditation-grant">
            <Plus size={14} className="mr-1.5" /> Accorder
          </Button>
        )}
      </div>
      {state === "loading" && <Skeleton className="h-12 w-full" />}
      {state === "error" && (
        <div className="text-sm text-slate-600 space-y-2">
          <p>Impossible de charger les habilitations.</p>
          <Button variant="outline" size="sm" onClick={load}>Réessayer</Button>
        </div>
      )}
      {state === "ready" && (
        <>
          {current.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="accreditations-empty">
              Aucune habilitation en cours.{fiche.is_me && canGrant ? " Une habilitation pour vous-même doit être accordée par une autre personne." : ""}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="accreditations-current">
              {current.map((a) => <AccreditationRow key={a.id} item={a} refs={refs} onRevoke={allowed ? () => setRevoking(a) : null} />)}
            </ul>
          )}
          {past.length > 0 && (
            <div>
              <button type="button" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" onClick={() => setShowPast((v) => !v)}
                aria-expanded={showPast} data-testid="btn-accreditations-past">
                {showPast ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Expirées ou révoquées ({past.length})
              </button>
              {showPast && (
                <ul className="divide-y divide-slate-100 mt-1" data-testid="accreditations-past">
                  {past.map((a) => <AccreditationRow key={a.id} item={a} refs={refs} onRevoke={allowed && a.state === "expired" ? () => setRevoking(a) : null} />)}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      <GrantDialog open={granting} onOpenChange={setGranting} fiche={fiche} refs={refs} onDone={async () => { setGranting(false); await done(); }} />
      <RevokeDialog item={revoking} onClose={() => setRevoking(null)} refs={refs} onDone={async () => { setRevoking(null); await done(); }} onConflict={load} />
    </Card>
  );
}

function AccreditationRow({ item, refs, onRevoke }) {
  const period = item.valid_until ? `du ${formatDate(item.valid_from)} au ${formatDate(item.valid_until)}` : `depuis le ${formatDate(item.valid_from)}, sans limite`;
  return (
    <li className="py-2 flex items-start justify-between gap-3" data-testid={`accreditation-${item.id}`}>
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-slate-900 break-words">{item.skill_label}</span>
          <span className="text-xs text-slate-500">{refs.levelLabel(item.level)}</span>
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ACCREDITATION_STATE_CLASSES[item.state]}`} data-testid="accreditation-state">
            {ACCREDITATION_STATE_LABELS[item.state]}
          </span>
        </div>
        <div className="text-xs text-slate-500">Valable {period} · accordée par {item.granted_by_name}</div>
        {item.evidence && <div className="text-xs text-slate-600 break-words">Preuve : {item.evidence}</div>}
        {item.state === "revoked" && (
          <div className="text-xs text-slate-600 break-words">
            Révoquée par {item.revoked_by_name} le {formatDate(item.revoked_at)}{item.revoke_reason ? ` : « ${item.revoke_reason} »` : ""}
          </div>
        )}
      </div>
      {onRevoke && (
        <Button variant="ghost" size="sm" className="text-slate-600 shrink-0" onClick={onRevoke} data-testid={`btn-accreditation-revoke-${item.id}`}>Révoquer</Button>
      )}
    </li>
  );
}

const EMPTY_GRANT = { skill_id: "", level: "autonomous", valid_from: "", valid_until: "", evidence: "" };

function GrantDialog({ open, onOpenChange, fiche, refs, onDone }) {
  const [form, setForm] = useState(EMPTY_GRANT);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY_GRANT, valid_from: today() });
  }, [open]);

  const field = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const submit = async () => {
    if (!isGrantFormValid(form) || busy) return;
    setBusy(true);
    try {
      await api.post(`/collaborators/${fiche.id}/accreditations`, {
        skill_id: form.skill_id, level: form.level, valid_from: form.valid_from, valid_until: form.valid_until || null, evidence: form.evidence.trim() || null,
      });
      toast.success("Habilitation accordée");
      await onDone();
    } catch (error) {
      const info = errorInfo(error);
      toast.error(info.data?.msg || errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="accreditation-grant-dialog">
        <DialogHeader>
          <DialogTitle>Accorder une habilitation</DialogTitle>
          <DialogDescription>{fiche.display_name} sera autorisé(e) à intervenir au nom de Jamora pour cette compétence. Vous en serez l'auteur ; l'habilitation pourra être révoquée, jamais effacée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Compétence *</Label>
            <Select value={form.skill_id || undefined} onValueChange={(v) => setForm({ ...form, skill_id: v })}>
              <SelectTrigger data-testid="select-accreditation-skill"><SelectValue placeholder="Choisir une compétence" /></SelectTrigger>
              <SelectContent>{refs.skills.filter((s) => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Niveau *</Label>
            <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
              <SelectTrigger data-testid="select-accreditation-level"><SelectValue /></SelectTrigger>
              <SelectContent>{LEVEL_CODES.map((code) => <SelectItem key={code} value={code}>{refs.levelLabel(code)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="acc-from">Valable à partir du *</Label>
              <Input id="acc-from" type="date" className="text-base md:text-sm" value={form.valid_from} onChange={field("valid_from")} data-testid="input-accreditation-from" /></div>
            <div className="space-y-2"><Label htmlFor="acc-until">Jusqu'au (facultatif)</Label>
              <Input id="acc-until" type="date" min={form.valid_from || undefined} className="text-base md:text-sm" value={form.valid_until} onChange={field("valid_until")} data-testid="input-accreditation-until" /></div>
          </div>
          {form.valid_until && form.valid_until < form.valid_from && <p className="text-xs text-amber-700">La fin de validité ne peut pas précéder son début.</p>}
          <div className="space-y-2"><Label htmlFor="acc-evidence">Preuve</Label>
            <Textarea id="acc-evidence" rows={2} maxLength={2000} className="text-base md:text-sm" placeholder="Référence, lien ou observation qui justifie cette décision"
              value={form.evidence} onChange={field("evidence")} data-testid="input-accreditation-evidence" /></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!isGrantFormValid(form) || busy}  data-testid="btn-accreditation-grant-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Accorder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({ item, onClose, refs, onDone, onConflict }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(null); // keeps the text while the dialog fades out

  useEffect(() => {
    if (item) {
      setShown(item);
      setReason("");
    }
  }, [item]);

  const valid = reason.trim().length >= MIN_REVOKE_REASON;
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await api.post(`/accreditations/${shown.id}/revoke`, { expected_version: shown.version, reason: reason.trim() });
      toast.success("Habilitation révoquée");
      await onDone();
    } catch (error) {
      const info = errorInfo(error);
      toast.error(info.data?.msg || errorMessage(error));
      if (info.code === "version_conflict" || info.code === "already_revoked") {
        onClose();
        await onConflict();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="accreditation-revoke-dialog">
        <DialogHeader>
          <DialogTitle>Révoquer l'habilitation « {shown?.skill_label} » ({shown && refs.levelLabel(shown.level)}) ?</DialogTitle>
          <DialogDescription>Elle cesse de valoir immédiatement. Elle reste visible sur la fiche, avec votre nom, la date et le motif.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="acc-reason">Motif *</Label>
          <Textarea id="acc-reason" rows={2} maxLength={200} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-accreditation-reason" />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!valid || busy}  data-testid="btn-accreditation-revoke-confirm">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Révoquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
